/**
 * The battery display, and why its colours are not the landing threshold (#629).
 *
 * These two scales answer different questions and must not be merged:
 *
 * - **The colour bands** are a human-attention scale. They say where a pilot
 *   should start paying attention, they are the same for every aircraft in the
 *   fleet, and they do not move.
 * - **The landing threshold** is where the aircraft stops asking and puts itself
 *   down. It is per drone, it is expressed in volts per cell because that is what
 *   the autopilot decides on, and an operator sets it.
 *
 * A drone set to land at 10 % sitting at 16 % is amber here — and it is six points
 * from putting itself down. One set to 30 % looks the same shade and is already
 * descending. That is exactly why {@link BATTERY_AMBER_PERCENT} and
 * {@link BATTERY_RED_PERCENT} are fixed and why the display has to *say* where the
 * aircraft's own limit is, rather than encode it in a colour. See BatteryStatus.
 *
 * The obvious simplification later is to colour the bar from the drone's
 * threshold. `batteryBandsIndependent.test.ts` exists to stop that: it would
 * silently redefine what amber means, differently per aircraft.
 */
export type BatteryLevel = 'high' | 'normal' | 'amber' | 'red' | 'unknown';

/**
 * At or above this, the reading is green: plenty of flight left.
 *
 * It is a comfort band rather than a safety one. Nothing is decided here and no
 * threshold an operator can set reaches this high — the ceiling is about half the
 * pack — so green means "you are not thinking about the battery yet", which is
 * worth saying on a fleet view where most aircraft are in exactly that state.
 *
 * 40 % rather than the 50 % this used to sit at: a pack between 40 and 50 is not
 * meaningfully different from one above 50 for any decision a pilot makes, and
 * putting the line at the midpoint made half of a healthy fleet look like it
 * needed attention.
 */
export const BATTERY_HIGH_PERCENT = 40;

/**
 * Below this, the reading is amber: worth a look.
 *
 * It is the same number as the fleet default landing threshold, and that is a
 * coincidence of history rather than a dependency — SR-1 carried 20 % for years,
 * so it is also where a pilot learned to start watching. The two are free to
 * diverge: this one is a fleet-wide display rule and the other is per drone.
 */
export const BATTERY_AMBER_PERCENT = 20;

/**
 * Below this, the reading is red: act.
 *
 * Below every threshold an operator can set, which is the property that matters —
 * a red bar means "low whatever this aircraft was configured to", so it is never
 * reassuring for the wrong reason.
 */
export const BATTERY_RED_PERCENT = 15;

/**
 * The attention band for a charge, or `unknown` when nothing has been reported.
 *
 * Four bands, and only the lower two mean anything is wrong. `high` and `normal`
 * are both "nothing to do"; they are distinguished because a fleet view of twenty
 * aircraft is easier to read when the ones with most of their pack left are
 * obvious at a glance.
 *
 * Unknown is not low (#577). A drone that has never measured its battery renders
 * as N/A, never as 0 % and never as red — telling an operator their pack is flat
 * when nobody has read it sends them to a charger instead of to the link between
 * the companion and the flight controller.
 *
 * It takes a charge and nothing else. Passing it a drone, or a threshold, is how
 * the two scales would get merged.
 */
export const getBatteryLevel = (percentage: number | null): BatteryLevel => {
  if (percentage === null || percentage === undefined) {
    return 'unknown';
  }
  if (percentage < BATTERY_RED_PERCENT) {
    return 'red';
  }
  if (percentage < BATTERY_AMBER_PERCENT) {
    return 'amber';
  }
  if (percentage < BATTERY_HIGH_PERCENT) {
    return 'normal';
  }
  return 'high';
};

/**
 * The colour for a band.
 *
 * A gradient rather than four unrelated hues: green, light green, amber, red. The
 * two "nothing to do" bands are both green so neither reads as a warning, and the
 * step between them is a shade rather than a different colour — which is also why
 * it is safe for a viewer who cannot tell them apart. The step that has to be
 * unmistakable is normal to amber, and it is.
 */
export const getBatteryColor = (level: BatteryLevel, isDarkMode: boolean): string => {
  const colors: Record<BatteryLevel, string> = {
    high: isDarkMode ? '#66bb6a' : '#4caf50',
    normal: isDarkMode ? '#9ccc65' : '#8bc34a',
    amber: isDarkMode ? '#ffa726' : '#ff9800',
    red: isDarkMode ? '#ef5350' : '#f44336',
    unknown: isDarkMode ? '#757575' : '#9e9e9e',
  };
  return colors[level];
};

/**
 * The words that go with the colour.
 *
 * Colour is never the only carrier of the warning (WCAG AA, and `ui/CLAUDE.md`).
 * An operator with a colour vision deficiency, or a washed-out screen in
 * sunlight at a field test, gets the same information from the text.
 */
export const getBatteryLevelLabel = (level: BatteryLevel): string => {
  const labels: Record<BatteryLevel, string> = {
    high: 'Good',
    normal: 'Normal',
    amber: 'Low',
    red: 'Critical',
    unknown: 'Not reported',
  };
  return labels[level];
};

export const formatBatteryPercentage = (percentage: number | null): string => {
  if (percentage === null || percentage === undefined) {
    return 'N/A';
  }
  return `${Math.round(percentage)}%`;
};

export const estimateTimeToLanding = (
  percentage: number,
  depletionRate: number
): number | null => {
  if (!percentage || !depletionRate || depletionRate <= 0) {
    return null;
  }
  // Time in minutes
  return (percentage / depletionRate) * 60;
};
